import React, {
    FormEvent,
    ReactElement, useState,
    useEffect,
} from "react";
import { Form } from "react-bootstrap";
import { GoVerified } from "react-icons/go";
import { MdOutlinePolicy, MdOutlinePrivacyTip } from "react-icons/md";
import { BsLink, BsShieldCheck } from "react-icons/bs";
import { GrValidate } from "react-icons/gr";
import { AiOutlineFileProtect } from "react-icons/ai";
import { BiSupport } from "react-icons/bi";
import Confirm from "../../../../components/confirm/Confirm";
import { HtmlRenderer } from "../../../../components/html-render/HtmlRenderer";
import Loading from "../../../../components/loading/Loading";
import TimedMessage from "../../../../components/timed-message/TimedMessage";
import FieldTypeValueDisplay from "../../../../components/form-field/FieldTypeValueDisplay";
import useRouting, { useParams } from "../../../../contexts/Routing";
import {
    GetWorkflowServiceDocument, GetWorkflowServicesDocument,
    Integration,
    useGetWorkflowServiceQuery, useInstallWorkflowServiceMutation,
    useUninstallWorkflowServiceMutation, WorkflowService as ApiWorkflowService,
    FieldTypedValue,
    useSaveIntegrationSettingsMutation,
    FormField,
    IntegrationActionType,
    GetAllIntegrationsDocument,
} from "../../../../__generated__/api";
import { NoDataOrError } from "../../../error/Error";
import UserGroups from "../../../../components/pickers/user-groups/UserGroups";
import { removeTypenameList } from "../../../../api";
import useDirtyTracker from "../../../../components/dirty-tracker/DirtyTracker";

interface ServiceOptionsProps {
    service: ApiWorkflowService;
    integration: Integration;
    onUninstall: (id: string) => void;
}

function copySetting(value: FieldTypedValue): FieldTypedValue {
    const copy = { ...value };
    delete copy.__typename;
    return copy;
}

const PERMISSION_GROUPS: Record<string, {text: string; description: string}> = {
    [IntegrationActionType.Execute]: {
        text: "Execute actions",
        description: "This integration allows users to execute certain actions from the ticket screen. "
        + "This controls which users are able to access this integration.",
    },
    [IntegrationActionType.UserDataProvider]: {
        text: "View User data",
        description: "This integration shows information about the user in the left hand ticket side. "
        + "This setting controls which users have access to view user information through this integration.",
    },
};

function LearnMore({ service, className }: {
    service: ApiWorkflowService;
    className: string;
}) {
    return (
        <div className={className}>
            {service.learnMore
            && (service.learnMore.websiteUrl || service.learnMore.supportCenterUrl || service.learnMore.termsOfServiceUrl)
            && (

                <div className="mt-3 w-100  ps-0 list-group list-group-flush">
                    <strong>Learn More</strong>
                    <small>
                        {service.learnMore.websiteUrl && (
                            <a
                                className="list-group-item border-0  fw-light p-1"
                                href={service.learnMore?.websiteUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <BsLink />
                                {" "}
                                Partner Website
                            </a>
                        )}

                        {service.learnMore.supportCenterUrl && (
                            <a
                                className="list-group-item border-0  fw-light p-1"
                                href={service.learnMore?.supportCenterUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <BiSupport />
                                {" "}
                                Get Support
                            </a>
                        )}
                        {service.learnMore.termsOfServiceUrl && (
                            <a
                                className="list-group-item border-0 fw-light p-1"
                                href={service.learnMore?.termsOfServiceUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <MdOutlinePolicy />
                                {" "}
                                Terms of Service
                            </a>
                        )}
                    </small>
                </div>
            )}
            {service.learnMore
            && (service.learnMore.privacyPolicyUrl
                || service.learnMore.dataProtectionUrl
                || service.learnMore.securityCenterUrl)
            && (
                <div className="mt-3 w-100  ps-0 list-group list-group-flush">
                    <strong>Privacy & Security</strong>
                    <small>
                        {service.learnMore.privacyPolicyUrl && (
                            <a
                                className="list-group-item border-0 fw-light p-1"
                                href={service.learnMore?.privacyPolicyUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <MdOutlinePrivacyTip />
                                {" "}
                                Privacy Policy
                            </a>
                        )}

                        {service.learnMore.dataProtectionUrl && (
                            <a
                                className="list-group-item border-0  fw-light p-1"
                                href={service.learnMore?.dataProtectionUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <AiOutlineFileProtect />
                                {" "}
                                Data Protection
                            </a>
                        )}

                        {service.learnMore.securityCenterUrl && (
                            <a
                                className="list-group-item border-0  fw-light p-1"
                                href={service.learnMore?.securityCenterUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <BsShieldCheck />
                                {" "}
                                Security Center
                            </a>
                        )}
                    </small>
                </div>

            )}
        </div>
    );
}

function ServiceOptions({
    integration, service, onUninstall,
}: ServiceOptionsProps) {
    const [optionValues, setOptionValues] = useState<FieldTypedValue[]>(() => integration.settings?.map(copySetting) ?? []);

    const [save] = useSaveIntegrationSettingsMutation();
    const [actionPermissions, setActionPermissions] = useState(removeTypenameList(integration.actionPermissions ?? []));

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: `${integration.label} settings saved!`,
        onSave: (event, onDone) => {
            event.preventDefault();
            (async () => {
                await save({
                    refetchQueries: [
                        { query: GetWorkflowServiceDocument, variables: { id: service.id } },
                    ],
                    variables: {
                        settings: {
                            id: integration.id,
                            settings: optionValues,
                            actionPermissions,
                        },
                    },
                });
                onDone();
            })();
        },
    });

    dirtyTracker.useWatchDeepCompareProperty(optionValues, integration.settings);
    dirtyTracker.useWatchDeepCompareProperty(actionPermissions ?? [], integration.actionPermissions ?? []);

    dirtyTracker.useLoad(() => {
        setOptionValues(integration.settings?.map(copySetting) ?? []);
        setActionPermissions(removeTypenameList(integration.actionPermissions ?? []));
    }, [integration]);

    const valuesMap = optionValues.toIdMap("fieldId") ?? {};

    const hasAction = service.actionTypes?.some(type => PERMISSION_GROUPS[type]);
    const noOptions = !hasAction && service.options.isEmpty;

    return (
        <form className="d-flex h-100" key={integration.id}>

            {noOptions ? (
                <div className="text-light col h-100 p-3">
                    <em>No options</em>
                </div>
            ) : (

                <div className="col d-flex flex-column h-100">

                    {service.options.map(group => (
                        <div key={group.title} className="mt-3 mb-5 border p-4 rounded border-light">
                            <h5>
                                {group.title}
                            </h5>
                            {group.description && (
                                <HtmlRenderer className="mb-4 text-gray-500 " html={group.description ?? ""} />
                            )}
                            {group.options.map(option => {
                                const field = option.input;
                                const value = valuesMap[field?.id] ?? field?.defaultValue;
                                return (
                                    <div key={field?.id ?? option.instructions?.substr(0, 20)}>
                                        {option.instructions && (
                                            <HtmlRenderer className="mb-4 text-gray-500 mx-2" html={option.instructions ?? ""} />
                                        )}
                                        {field && (
                                            <FieldTypeValueDisplay
                                                className="mt-4 mx-2"
                                                inputClassName="form-control my-2"
                                                field={field}
                                                value={value}
                                                integrationId={integration.id}
                                                getCurrentForm={() => optionValues}
                                                onChange={changedValue => {
                                                    const index = optionValues.findIndex(row => row.fieldId === field.id);
                                                    if (index >= 0) {
                                                        optionValues[index] = changedValue;
                                                    } else {
                                                        optionValues.push(changedValue);
                                                    }
                                                    setOptionValues(Array.from(optionValues));
                                                }}
                                            />
                                        )}

                                    </div>
                                );
                            })}

                        </div>
                    ))}

                    {hasAction && (
                        <div className="mt-3 mb-5 border p-4 rounded border-light">
                            <h5>
                                Permissions
                            </h5>

                            <div>
                                Control which users are able to access this integration by
                                selecting which user groups are able to handle the actions below.
                            </div>

                            {service.actionTypes?.sortBy(row => row).map(type => {
                                if (!PERMISSION_GROUPS[type]) {
                                    return null;
                                }
                                const { text, description } = PERMISSION_GROUPS[type];
                                const permission = actionPermissions?.find(group => group.action === type);

                                return (
                                    <div key={type} className="mt-4">
                                        <strong className="d-block">
                                            {text}
                                        </strong>
                                        <small className="text-gray-600 d-block mb-1">
                                            {description}
                                        </small>
                                        <div className="d-flex align-items-center">

                                            <Form.Check
                                                type="switch"
                                                name={`picker-${type}`}
                                                label={permission == null ? "Everyone" : "Specific Groups"}
                                                className="ms-2 col-2"
                                                checked={permission == null}
                                                onChange={evt => {
                                                    if (evt.target.checked) {
                                                        const copy = actionPermissions.filter(row => row.action !== type);
                                                        setActionPermissions(copy);
                                                    } else {
                                                        const copy = [...actionPermissions];
                                                        copy.push({
                                                            action: type,
                                                            groupIds: [],
                                                        });
                                                        setActionPermissions(copy);
                                                    }
                                                }}
                                            />

                                            {permission && (
                                                <UserGroups
                                                    isMulti
                                                    className="w-100"
                                                    onSelected={groupIds => {
                                                        const copy = [
                                                            ...(actionPermissions ?? []),
                                                        ];
                                                        const current = copy?.find(group => group.action === type);
                                                        if (current) {
                                                            current.groupIds = groupIds;
                                                        } else {
                                                            copy.push({
                                                                action: type,
                                                                groupIds,
                                                            });
                                                        }
                                                        setActionPermissions(copy);
                                                    }}
                                                    value={permission?.groupIds}
                                                />
                                            )}
                                        </div>

                                    </div>
                                );
                            })}

                        </div>
                    )}

                    {dirtyTracker.saveComponent}

                </div>
            )}
            <div className={`col-2 ps-3 ms-3 text-center h-100 d-flex flex-column ${noOptions ? "border-start" : ""}`}>

                <img
                    src={service.iconUrl as string}
                    className="w-100"
                    alt={`Icon for ${service.name}`}
                />
                <div className="flex-grow-1 d-flex flex-column justify-content-between">
                    <div>
                        <span className="text-success mt-3 d-block flex-row-1 border border-success rounded py-2">
                            <GoVerified />
                            {" "}
                            Installed
                        </span>

                        <LearnMore service={service} className="text-start mt-3" />

                    </div>

                    <div>
                        <button
                            type="button"
                            className="btn btn-none mt-4 text-danger me-3"
                            onClick={() => onUninstall(integration.id)}
                        >
                            Uninstall
                        </button>

                    </div>

                </div>

            </div>
        </form>
    );
}

interface ServiceAboutProps {
    service: ApiWorkflowService;
    onInstall: () => void;
}

function ServiceAbout({ service, onInstall }: ServiceAboutProps): ReactElement {
    const buttonText = `${service.connection.buttonText ?? "Install"} ${service.installedIntegrations.isNotEmpty ? "again" : ""}`;
    const button = service.connection.url ? (
        <a
            type="button"
            className="btn btn-primary mt-3"
            href={service.connection.url.url}
            target="_blank"
            rel="noopener noreferrer"
        >
            {buttonText}
        </a>
    ) : (
        <button
            type="button"
            className="btn btn-primary mt-3"
            onClick={() => onInstall()}
        >
            {buttonText}
        </button>
    );
    return (
        <div className="d-flex h-100">
            <div className="col d-flex flex-column h-100">

                <h5 className="mb-3 text-light flex-grow-0">
                    {service.name}
                </h5>
                <HtmlRenderer className="flex-grow-1" html={service.longDescription ?? ""} />
                {service.actions && (
                    <div className="mt-4 pt-3 border-top flex-grow-0">
                        <h5 className="">
                            Available Actions
                        </h5>
                        <ul className="list-group list-group-flush d-flex flex-wrap flex-row">
                            {service.actions.map(action => (
                                <li className="list-group-item border-0 px-2 col-4" key={action.id}>
                                    <div>
                                        <strong>{action.name}</strong>
                                    </div>
                                    <small>
                                        {action.description}
                                    </small>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
            <div className="col-2 border-start ps-3 ms-3 text-center h-100 d-flex flex-column">
                <img
                    src={service.iconUrl as string}
                    className="w-100"
                    alt={`Icon for ${service.name}`}
                />
                {button}
                <LearnMore service={service} className="text-start" />

            </div>
        </div>
    );
}

export function WorkflowServiceInstall({
    service, onDone, isInstalling, onCompletedUrl,
}: {
    service: ApiWorkflowService;
    onDone: () => void;
    onCompletedUrl?: string;
    isInstalling: boolean
}): ReactElement {
    const [apiKey, setApiKey] = useState("");
    const [installFields, setInstallFields] = useState<FieldTypedValue[]>([]);

    const id = service.id as string;
    const [install] = useInstallWorkflowServiceMutation({
        refetchQueries: [
            { query: GetWorkflowServiceDocument, variables: { id } },
            { query: GetWorkflowServicesDocument },
            { query: GetAllIntegrationsDocument },

        ],
    });

    const confirmSetup = () => {
        onDone();
        if (!service) {
            return;
        }
        (async () => {
            const response = await install({
                variables: {
                    install: {
                        id,
                        apiKey: !service?.connection.apiKey ? undefined : {
                            input: installFields,
                            apiKey,
                        },
                        manual: !service.connection.manual ? undefined : {
                            input: installFields,
                        },
                        oauth: !service?.connection.oauth ? undefined : {
                            input: installFields,
                            redirectUrl: onCompletedUrl ?? window.location.href,
                        },
                    },
                },
            });
            const redirect = response.data?.workflowServiceInstall.redirectUrl;
            if (redirect) {
                window.open(redirect);
            }
        })();
    };
    const serviceConnection = service.connection.apiKey ?? service.connection.manual;

    return (
        <Confirm
            shouldShow={isInstalling}
            confirmText="Install"
            closeText="Cancel"
            onHide={() => onDone()}
            onConfirm={() => confirmSetup()}
        >
            <div>
                <h5 className="mb-3">
                    Install
                    {" "}
                    {service.name}
                </h5>
                {serviceConnection && (
                    <div>
                        <Form.Group className="my-3">
                            {service.connection.apiKey && (
                                <>
                                    <Form.Label className="fw-bolder">
                                        {service.connection.apiKey.apiKeyFieldName ?? "API Key"}
                                    </Form.Label>
                                    <input
                                        type="password"
                                        placeholder={service.connection.apiKey.placeholder ?? "abc123"}
                                        required
                                        onChange={evt => setApiKey(evt.target.value)}
                                        value={apiKey}
                                        className="bottom-line"
                                    />
                                    <Form.Text>
                                        <HtmlRenderer html={service.connection.apiKey.instructions} />
                                    </Form.Text>
                                </>
                            )}

                            {serviceConnection.input?.map(field => (
                                <FieldTypeValueDisplay
                                    className="mt-3"
                                    field={field as FormField}
                                    onChange={changedValue => {
                                        const index = installFields.findIndex(row => row.fieldId === field.id);
                                        if (index >= 0) {
                                            installFields[index] = changedValue;
                                        } else {
                                            installFields.push(changedValue);
                                        }

                                        setInstallFields(Array.from(installFields));
                                    }}
                                />

                            ))}
                        </Form.Group>
                    </div>
                )}
                {service.connection.oauth && (
                    <div>
                        {service.connection.oauth.instructions && (
                            <div className="mb-4 text-gray-500">
                                {service.connection.oauth.instructions}
                            </div>
                        )}

                        {service.connection.oauth.input?.map(field => (
                            <FieldTypeValueDisplay
                                className="mt-3"
                                field={field as FormField}
                                onChange={changedValue => {
                                    const index = installFields.findIndex(row => row.fieldId === field.id);
                                    if (index >= 0) {
                                        installFields[index] = changedValue;
                                    } else {
                                        installFields.push(changedValue);
                                    }

                                    setInstallFields(Array.from(installFields));
                                }}
                            />

                        ))}
                    </div>
                )}
            </div>
        </Confirm>
    );
}
WorkflowServiceInstall.defaultProps = {
    onCompletedUrl: undefined,
};

export default function WorkflowService(): ReactElement {
    const { Paths, getSearchParams, historyPushSearch } = useRouting();
    const [isInstalling, setIsInstalling] = useState(false);

    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    const searchParams = getSearchParams(Paths.ADMINISTRATOR_SETTING_ID);
    const { error: installError, isSuccess: installSuccess } = searchParams;

    const [uninstall] = useUninstallWorkflowServiceMutation({
        refetchQueries: [
            { query: GetWorkflowServiceDocument, variables: { id } },
            { query: GetWorkflowServicesDocument },
            { query: GetAllIntegrationsDocument },

        ],
    });

    const [uninstallId, setUninstallId] = useState<string>();
    const [currentTab, setCurrentTab] = useState<string>();

    const { data: services, loading: isLoading, error } = useGetWorkflowServiceQuery({
        skip: !id,
        variables: {
            id,
        },
    });

    if (isLoading) {
        return <Loading />;
    }
    if (error || !services?.workflowService) {
        return <NoDataOrError error={error} />;
    }
    const service = services?.workflowService;

    const confirmUninstall = () => {
        setUninstallId(undefined);
        if (!service) {
            return;
        }
        (async () => {
            await uninstall({
                variables: {
                    integrationId: uninstallId,
                },
            });
        })();
    };

    return (
        <div className="border p-4 rounded mb-5 bg-white h-100">

            <TimedMessage
                shouldShow={installError != null && installError !== ""}
                title="Error while installing"
                variant="danger"
                onClose={() => {
                    delete (searchParams as Partial<typeof searchParams>).error;
                    historyPushSearch(searchParams);
                }}
            >
                {installError}
            </TimedMessage>

            <TimedMessage
                shouldShow={installSuccess === "✔"}
                title={`${service.name} Install Successful`}
                onClose={() => {
                    delete (searchParams as Partial<typeof searchParams>).isSuccess;
                    historyPushSearch(searchParams);
                }}
            >
                <>
                    Successfully installed
                    {" "}
                    {service.name}
                    !
                </>
            </TimedMessage>

            <WorkflowServiceInstall
                service={service as ApiWorkflowService}
                isInstalling={isInstalling}
                onDone={() => setIsInstalling(false)}
            />
            <Confirm
                confirmText="Uninstall"
                onConfirm={confirmUninstall}
                shouldShow={uninstallId != null}
                onHide={() => setUninstallId(undefined)}
                confirmActionText={service.installedIntegrations.find(int => int.id === uninstallId)?.label}
                closeText="Cancel"
                confirmVariant="danger"
            >
                <div>
                    Are you sure you want to uninstall:
                    {" "}
                    <div className="mt-2 p-2 rounded alert alert-danger">
                        {service.installedIntegrations.find(int => int.id === uninstallId)?.label}
                    </div>
                </div>
            </Confirm>

            {service.installedIntegrations.isNotEmpty ? (
                <div className="h-100 d-flex flex-column">
                    <ul className="nav nav-pills mb-3 flex-grow-0">
                        {service.installedIntegrations.map((integration, index) => (
                            <li className="nav-item" key={integration.id}>
                                <button
                                    type="button"
                                    className={`nav-link text-gray-600 ${
                                        (currentTab === integration.id || (currentTab == null && index === 0))
                                            ? "list-border-primary list-border-bottom pb-2 fw-bolder"
                                            : ""}`}
                                    onClick={() => setCurrentTab(integration.id)}
                                >

                                    {integration.label}
                                </button>
                            </li>
                        ))}
                        <li className="nav-item">
                            <button
                                type="button"
                                className={`nav-link text-gray-600 ${currentTab === "about"
                                    ? "list-border-primary list-border-bottom pb-2 fw-bolder"
                                    : ""}`}
                                onClick={() => setCurrentTab("about")}
                            >
                                About
                            </button>
                        </li>

                    </ul>
                    <div className=" flex-grow-1">

                        {currentTab === "about" ? (
                            <ServiceAbout service={service as ApiWorkflowService} onInstall={() => setIsInstalling(true)} />
                        ) : (
                            <ServiceOptions
                                service={service as ApiWorkflowService}
                                integration={service.installedIntegrations.find(integration => integration.id === currentTab)
                                    ?? service.installedIntegrations.firstElement}
                                onUninstall={integrationId => setUninstallId(integrationId)}
                            />
                        )}
                    </div>
                </div>

            ) : (
                <ServiceAbout service={service as ApiWorkflowService} onInstall={() => setIsInstalling(true)} />
            )}

        </div>

    );
}
